import { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../ui/alert-dialog';
import { Switch } from '../../ui/switch';
import {
  ArrowLeft, Building2, MapPin, Users, Edit, Save, X, Plus, Trash2,
  Calendar, Loader2, AlertTriangle, CheckCircle, Shield, Briefcase,
  Clock, User, ChevronRight, DollarSign
} from 'lucide-react';
import { useAuth } from '../../../src/hooks/useAuth';
import { useOrgUsers } from '../../../src/hooks/queries/use-org-users';
import { useProjects } from '../../../src/hooks/queries/use-projects';
import { projectService, Project, ProjectLocation, ProjectTeamMember, ApprovalModule } from '../../../src/services/project.service';
import { queryKeys } from '../../../src/lib/query-keys';
import { showSuccess, showError } from '../../../src/utils/notification.utils';
import { useAppNav } from '../../../src/contexts/app-nav.context';
import { formatCurrencyCompact } from '../../../src/utils/currency';
import { useOrgCurrency } from '../../../src/hooks/useOrgCurrency';
import { BudgetTab } from './ProjectBudgetTab';

// ─── Constants ────────────────────────────────────────────────────────────────

const LOCATION_TYPES = ['site_office', 'storage_yard', 'work_area', 'laydown', 'subcontractor_office', 'other'];

const PROJECT_ROLES = [
  'Project Manager', 'Site Engineer', 'Site Supervisor', 'Safety Officer',
  'QA/QC Engineer', 'Quantity Surveyor', 'Procurement Officer', 'Finance Officer',
  'Architect', 'Foreman', 'Administrator', 'Other',
];

const APPROVAL_MODULE_LABELS: Record<ApprovalModule, string> = {
  purchase_orders: 'Purchase Orders',
  invoices: 'Invoices',
  change_orders: 'Change Orders',
  rfis: 'RFIs',
  contracts: 'Contracts',
  budgets: 'Budgets',
};

const ALL_MODULES: ApprovalModule[] = [
  'purchase_orders', 'invoices', 'change_orders', 'rfis', 'contracts', 'budgets',
];

function statusColor(status: string) {
  switch (status) {
    case 'active': return 'bg-blue-600 text-white';
    case 'completed': return 'bg-green-600 text-white';
    case 'planning': return 'bg-purple-600 text-white';
    case 'on_hold': return 'bg-yellow-100 text-yellow-800';
    case 'delayed': return 'bg-orange-500 text-white';
    case 'cancelled': return 'bg-red-500 text-white';
    default: return '';
  }
}

// ─── Location Modal ───────────────────────────────────────────────────────────

interface LocationModalProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  projectId: string;
  location?: ProjectLocation;
  onSaved: () => void;
}

function LocationModal({ open, onOpenChange, projectId, location, onSaved }: LocationModalProps) {
  const isEdit = !!location;
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    location_name: location?.location_name ?? '',
    location_type: location?.location_type ?? '',
    address: location?.address ?? '',
    city: location?.city ?? '',
    country: location?.country ?? '',
    start_date: location?.start_date ?? '',
    end_date: location?.end_date ?? '',
    notes: location?.notes ?? '',
  });

  useEffect(() => {
    if (open) {
      setForm({
        location_name: location?.location_name ?? '',
        location_type: location?.location_type ?? '',
        address: location?.address ?? '',
        city: location?.city ?? '',
        country: location?.country ?? '',
        start_date: location?.start_date ?? '',
        end_date: location?.end_date ?? '',
        notes: location?.notes ?? '',
      });
    }
  }, [open, location]);

  const handleSave = async () => {
    if (!form.location_name.trim()) { showError({ title: 'Validation', message: 'Location name is required' }); return; }
    setSaving(true);
    try {
      const payload = {
        location_name: form.location_name.trim(),
        location_type: form.location_type || undefined,
        address: form.address || undefined,
        city: form.city || undefined,
        country: form.country || undefined,
        start_date: form.start_date || undefined,
        end_date: form.end_date || undefined,
        notes: form.notes || undefined,
      };
      if (isEdit) {
        await projectService.updateProjectLocation(projectId, location!.location_id, payload);
        showSuccess({ title: 'Location Updated', message: form.location_name });
      } else {
        await projectService.createProjectLocation(projectId, payload);
        showSuccess({ title: 'Location Added', message: form.location_name });
      }
      onSaved();
      onOpenChange(false);
    } catch (err: any) {
      showError({ title: 'Failed to Save Location', message: err?.message ?? 'An error occurred' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px] modal-content">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            {isEdit ? 'Edit Location' : 'Add Location'}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid gap-2">
            <Label>Location Name *</Label>
            <Input value={form.location_name} onChange={e => setForm(p => ({ ...p, location_name: e.target.value }))} placeholder="e.g., Block A – Foundation Zone" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label>Type</Label>
              <Select value={form.location_type} onValueChange={v => setForm(p => ({ ...p, location_type: v }))}>
                <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent>{LOCATION_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label>Address</Label>
              <Input value={form.address} onChange={e => setForm(p => ({ ...p, address: e.target.value }))} placeholder="Street address" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label>City</Label>
              <Input value={form.city} onChange={e => setForm(p => ({ ...p, city: e.target.value }))} placeholder="City" />
            </div>
            <div className="grid gap-2">
              <Label>Country</Label>
              <Input value={form.country} onChange={e => setForm(p => ({ ...p, country: e.target.value }))} placeholder="Country" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label>Start Date</Label>
              <Input type="date" value={form.start_date} onChange={e => setForm(p => ({ ...p, start_date: e.target.value }))} />
            </div>
            <div className="grid gap-2">
              <Label>End Date</Label>
              <Input type="date" value={form.end_date} onChange={e => setForm(p => ({ ...p, end_date: e.target.value }))} />
            </div>
          </div>
          <div className="grid gap-2">
            <Label>Notes</Label>
            <Textarea value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} rows={2} placeholder="Additional details…" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="gap-2">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {isEdit ? 'Save Changes' : 'Add Location'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Team Member Modal ────────────────────────────────────────────────────────

interface TeamModalProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  projectId: string;
  member?: ProjectTeamMember;
  orgUsers: any[];
  existingUserIds: string[];
  onSaved: () => void;
}

function TeamMemberModal({ open, onOpenChange, projectId, member, orgUsers, existingUserIds, onSaved }: TeamModalProps) {
  const isEdit = !!member;
  const [saving, setSaving] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(member?.user_id ?? '');
  const [role, setRole] = useState(member?.project_role ?? '');
  const [approvalModules, setApprovalModules] = useState<ApprovalModule[]>(member?.approval_modules ?? []);
  const [approvalLimit, setApprovalLimit] = useState(member?.approval_limit != null ? String(member.approval_limit) : '');

  useEffect(() => {
    if (open) {
      setSelectedUserId(member?.user_id ?? '');
      setRole(member?.project_role ?? '');
      setApprovalModules(member?.approval_modules ?? []);
      setApprovalLimit(member?.approval_limit != null ? String(member.approval_limit) : '');
    }
  }, [open, member]);

  const toggleModule = (mod: ApprovalModule) => {
    setApprovalModules(prev =>
      prev.includes(mod) ? prev.filter(m => m !== mod) : [...prev, mod]
    );
  };

  const handleSave = async () => {
    if (!isEdit && !selectedUserId) { showError({ title: 'Validation', message: 'Select a team member' }); return; }
    if (!role.trim()) { showError({ title: 'Validation', message: 'Role is required' }); return; }
    setSaving(true);
    try {
      const payload = {
        project_role: role.trim(),
        approval_modules: approvalModules,
        approval_limit: approvalLimit ? Number(approvalLimit) : undefined,
      };
      if (isEdit) {
        await projectService.updateProjectTeamMember(projectId, member!.membership_id, payload);
        showSuccess({ title: 'Member Updated' });
      } else {
        await projectService.addProjectTeamMember(projectId, { user_id: selectedUserId, ...payload });
        showSuccess({ title: 'Member Added' });
      }
      onSaved();
      onOpenChange(false);
    } catch (err: any) {
      showError({ title: 'Failed to Save', message: err?.message ?? 'An error occurred' });
    } finally {
      setSaving(false);
    }
  };

  const availableUsers = orgUsers.filter(u => {
    const uid = u.user_id || u.id;
    return isEdit ? true : !existingUserIds.includes(uid);
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px] modal-content">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            {isEdit ? 'Edit Team Member' : 'Add Team Member'}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {!isEdit && (
            <div className="grid gap-2">
              <Label>Staff Member *</Label>
              <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                <SelectTrigger><SelectValue placeholder="Select staff member" /></SelectTrigger>
                <SelectContent>
                  {availableUsers.filter(u => u.user_id || u.id).map(u => {
                    const uid = u.user_id || u.id;
                    const name = u.full_name || `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim();
                    return <SelectItem key={uid} value={uid}>{name} {u.email ? `· ${u.email}` : ''}</SelectItem>;
                  })}
                </SelectContent>
              </Select>
            </div>
          )}
          {isEdit && (
            <p className="text-sm font-medium">{member?.full_name} <span className="text-muted-foreground">· {member?.email}</span></p>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label>Project Role *</Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger><SelectValue placeholder="Select role" /></SelectTrigger>
                <SelectContent>{PROJECT_ROLES.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label>Approval Limit (amount)</Label>
              <Input type="number" value={approvalLimit} onChange={e => setApprovalLimit(e.target.value)} placeholder="0 = unlimited" min="0" />
            </div>
          </div>
          <div className="grid gap-2">
            <Label className="flex items-center gap-1"><Shield className="h-3.5 w-3.5" />Approval Permissions</Label>
            <div className="grid grid-cols-2 gap-2 pt-1">
              {ALL_MODULES.map(mod => (
                <div key={mod} className="flex items-center gap-2 rounded-md border p-2">
                  <Switch
                    checked={approvalModules.includes(mod)}
                    onCheckedChange={() => toggleModule(mod)}
                    id={`mod-${mod}`}
                  />
                  <label htmlFor={`mod-${mod}`} className="text-sm cursor-pointer select-none">
                    {APPROVAL_MODULE_LABELS[mod]}
                  </label>
                </div>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="gap-2">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {isEdit ? 'Save Changes' : 'Add Member'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Locations Tab ────────────────────────────────────────────────────────────

function LocationsTab({ projectId }: { projectId: string }) {
  const [locations, setLocations] = useState<ProjectLocation[]>([]);
  const [loading, setLoading] = useState(true);
  const [apiAvailable, setApiAvailable] = useState(true);
  const [locationModal, setLocationModal] = useState(false);
  const [editingLocation, setEditingLocation] = useState<ProjectLocation | undefined>();
  const [deleteTarget, setDeleteTarget] = useState<ProjectLocation | null>(null);

  const loadLocations = async () => {
    setLoading(true);
    try {
      const data = await projectService.getProjectLocations(projectId);
      setLocations(data);
      setApiAvailable(true);
    } catch (err: any) {
      if (err?.status === 404 || err?.response?.status === 404) setApiAvailable(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadLocations(); }, [projectId]);

  const handleDelete = async (loc: ProjectLocation) => {
    try {
      await projectService.deleteProjectLocation(projectId, loc.location_id);
      showSuccess({ title: 'Location Removed', message: loc.location_name });
      loadLocations();
    } catch (err: any) {
      showError({ title: 'Failed to Remove', message: err?.message });
    }
    setDeleteTarget(null);
  };

  if (!apiAvailable) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <AlertTriangle className="h-8 w-8 text-yellow-500 mx-auto mb-3" />
          <p className="font-medium">Locations API not yet available</p>
          <p className="text-sm text-muted-foreground mt-1">Backend endpoint <code className="text-xs bg-muted px-1 rounded">/api/v1/projects/{'{id}'}/locations</code> is pending implementation.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">Define work zones, sub-sites, and areas with their active date ranges.</p>
        <Button size="sm" className="gap-2" onClick={() => { setEditingLocation(undefined); setLocationModal(true); }}>
          <Plus className="h-4 w-4" />Add Location
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : locations.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <MapPin className="h-8 w-8 text-muted-foreground/40 mx-auto mb-3" />
            <p className="font-medium text-muted-foreground">No locations defined</p>
            <p className="text-sm text-muted-foreground mt-1">Add work zones, sub-sites, and areas for this project.</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Location</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Address</TableHead>
                <TableHead>Start Date</TableHead>
                <TableHead>End Date</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {locations.map(loc => (
                <TableRow key={loc.location_id}>
                  <TableCell className="font-medium">{loc.location_name}</TableCell>
                  <TableCell>{loc.location_type ? <Badge variant="secondary">{loc.location_type}</Badge> : '—'}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{loc.address ?? '—'}</TableCell>
                  <TableCell className="text-sm">{loc.start_date ?? '—'}</TableCell>
                  <TableCell className="text-sm">{loc.end_date ?? '—'}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="icon" onClick={() => { setEditingLocation(loc); setLocationModal(true); }}>
                        <Edit className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="ghost" size="icon" className="text-destructive" onClick={() => setDeleteTarget(loc)}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <LocationModal
        open={locationModal}
        onOpenChange={setLocationModal}
        projectId={projectId}
        location={editingLocation}
        onSaved={loadLocations}
      />
      <AlertDialog open={!!deleteTarget} onOpenChange={v => { if (!v) setDeleteTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Location?</AlertDialogTitle>
            <AlertDialogDescription>"{deleteTarget?.location_name}" will be permanently removed from this project.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => deleteTarget && handleDelete(deleteTarget)} className="bg-destructive text-destructive-foreground">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ─── Team Tab ─────────────────────────────────────────────────────────────────

function TeamTab({ projectId, orgUsers }: { projectId: string; orgUsers: any[] }) {
  const [members, setMembers] = useState<ProjectTeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [apiAvailable, setApiAvailable] = useState(true);
  const [memberModal, setMemberModal] = useState(false);
  const [editingMember, setEditingMember] = useState<ProjectTeamMember | undefined>();
  const [removeTarget, setRemoveTarget] = useState<ProjectTeamMember | null>(null);

  const loadMembers = async () => {
    setLoading(true);
    try {
      const data = await projectService.getProjectTeamMembers(projectId);
      setMembers(data);
      setApiAvailable(true);
    } catch (err: any) {
      if (err?.status === 404 || err?.response?.status === 404) setApiAvailable(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadMembers(); }, [projectId]);

  const handleRemove = async (m: ProjectTeamMember) => {
    try {
      await projectService.removeProjectTeamMember(projectId, m.membership_id);
      showSuccess({ title: 'Member Removed', message: m.full_name });
      loadMembers();
    } catch (err: any) {
      showError({ title: 'Failed to Remove', message: err?.message });
    }
    setRemoveTarget(null);
  };

  if (!apiAvailable) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <AlertTriangle className="h-8 w-8 text-yellow-500 mx-auto mb-3" />
          <p className="font-medium">Team API not yet available</p>
          <p className="text-sm text-muted-foreground mt-1">Backend endpoint <code className="text-xs bg-muted px-1 rounded">/api/v1/projects/{'{id}'}/members</code> is pending implementation.</p>
        </CardContent>
      </Card>
    );
  }

  const existingUserIds = members.map(m => m.user_id);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">Add staff members and assign their project roles and approval permissions.</p>
        <Button size="sm" className="gap-2" onClick={() => { setEditingMember(undefined); setMemberModal(true); }}>
          <Plus className="h-4 w-4" />Add Member
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : members.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Users className="h-8 w-8 text-muted-foreground/40 mx-auto mb-3" />
            <p className="font-medium text-muted-foreground">No team members yet</p>
            <p className="text-sm text-muted-foreground mt-1">Add staff and assign roles and approval rights.</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Can Approve</TableHead>
                <TableHead>Limit</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {members.map(m => (
                <TableRow key={m.membership_id}>
                  <TableCell>
                    <div>
                      <p className="font-medium text-sm">{m.full_name}</p>
                      <p className="text-xs text-muted-foreground">{m.email}</p>
                    </div>
                  </TableCell>
                  <TableCell><Badge variant="outline">{m.project_role}</Badge></TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {m.approval_modules.length === 0
                        ? <span className="text-xs text-muted-foreground">None</span>
                        : m.approval_modules.map(mod => (
                          <Badge key={mod} variant="secondary" className="text-xs">{APPROVAL_MODULE_LABELS[mod]}</Badge>
                        ))
                      }
                    </div>
                  </TableCell>
                  <TableCell className="text-sm">{m.approval_limit != null ? m.approval_limit.toLocaleString() : 'Unlimited'}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="icon" onClick={() => { setEditingMember(m); setMemberModal(true); }}>
                        <Edit className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="ghost" size="icon" className="text-destructive" onClick={() => setRemoveTarget(m)}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Approval matrix summary */}
      {members.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2"><Shield className="h-4 w-4" />Approval Matrix</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-2">
              {ALL_MODULES.map(mod => {
                const approvers = members.filter(m => m.approval_modules.includes(mod));
                return (
                  <div key={mod} className="flex items-center justify-between py-1.5 border-b last:border-0 text-sm">
                    <span className="font-medium">{APPROVAL_MODULE_LABELS[mod]}</span>
                    {approvers.length === 0
                      ? <span className="text-muted-foreground text-xs">No approver assigned</span>
                      : <div className="flex items-center gap-1 flex-wrap justify-end">
                          {approvers.map(a => (
                            <Badge key={a.membership_id} variant="outline" className="text-xs">{a.full_name}</Badge>
                          ))}
                        </div>
                    }
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      <TeamMemberModal
        open={memberModal}
        onOpenChange={setMemberModal}
        projectId={projectId}
        member={editingMember}
        orgUsers={orgUsers}
        existingUserIds={existingUserIds}
        onSaved={loadMembers}
      />
      <AlertDialog open={!!removeTarget} onOpenChange={v => { if (!v) setRemoveTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Team Member?</AlertDialogTitle>
            <AlertDialogDescription>{removeTarget?.full_name} will be removed from this project team.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => removeTarget && handleRemove(removeTarget)} className="bg-destructive text-destructive-foreground">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ─── Overview Tab ─────────────────────────────────────────────────────────────

function field(label: string, value: string | number | null | undefined) {
  return (
    <div className="space-y-1">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-sm font-medium">{value ?? '—'}</p>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export function ProjectDetailPage({ projectId }: { projectId: string }) {
  const { navigate } = useAppNav();
  const { orgId } = useAuth();
  const orgCurrency = useOrgCurrency();
  const { data: rawProjects = [], isLoading } = useProjects(orgId);
  const { data: orgUsers = [] } = useOrgUsers(orgId);

  const rawProject = rawProjects.find((p: any) => p.project_id === projectId);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!rawProject) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <AlertTriangle className="h-12 w-12 text-muted-foreground/40" />
        <p className="text-muted-foreground">Project not found.</p>
        <Button variant="outline" onClick={() => navigate('/projects')}>Back to Projects</Button>
      </div>
    );
  }

  const p = rawProject as any;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/projects')} className="shrink-0 mt-0.5">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl font-bold">{p.project_name}</h1>
            {p.project_number && <span className="text-muted-foreground text-sm">#{p.project_number}</span>}
            <Badge className={statusColor(p.status)}>{p.status?.replace('_', ' ')}</Badge>
          </div>
          <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground flex-wrap">
            {p.client_name && <span className="flex items-center gap-1"><Briefcase className="h-3.5 w-3.5" />{p.client_name}</span>}
            {p.start_date && <span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" />{p.start_date}{p.end_date ? ` → ${p.end_date}` : ''}</span>}
            {p.city && <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{p.city}{p.country ? `, ${p.country}` : ''}</span>}
          </div>
        </div>
      </div>

      <Tabs defaultValue="overview">
        <TabsList className="grid w-full grid-cols-4 max-w-xl">
          <TabsTrigger value="overview" className="gap-1.5"><Building2 className="h-3.5 w-3.5" />Overview</TabsTrigger>
          <TabsTrigger value="locations" className="gap-1.5"><MapPin className="h-3.5 w-3.5" />Locations</TabsTrigger>
          <TabsTrigger value="team" className="gap-1.5"><Users className="h-3.5 w-3.5" />Team & Approvals</TabsTrigger>
          <TabsTrigger value="budget" className="gap-1.5"><DollarSign className="h-3.5 w-3.5" />Budget</TabsTrigger>
        </TabsList>

        {/* ── Overview ─────────────────────────────────────────── */}
        <TabsContent value="overview" className="space-y-4 mt-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
              <CardContent className="p-4">
                <p className="text-xs text-blue-600 font-medium">Progress</p>
                <p className="text-2xl font-bold text-blue-700">{p.completion_percentage ?? 0}%</p>
                <div className="mt-2 h-1.5 rounded-full bg-blue-200"><div className="h-full rounded-full bg-blue-600" style={{ width: `${p.completion_percentage ?? 0}%` }} /></div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
              <CardContent className="p-4">
                <p className="text-xs text-emerald-600 font-medium">Total Budget</p>
                <p className="text-2xl font-bold text-emerald-700">{p.total_budget ? formatCurrencyCompact(p.total_budget, orgCurrency) : '—'}</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
              <CardContent className="p-4">
                <p className="text-xs text-purple-600 font-medium">Priority</p>
                <p className="text-2xl font-bold text-purple-700 capitalize">{p.priority ?? '—'}</p>
              </CardContent>
            </Card>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <Card>
              <CardHeader className="pb-3"><CardTitle className="text-sm flex items-center gap-2"><Building2 className="h-4 w-4 text-primary" />Project Details</CardTitle></CardHeader>
              <CardContent className="grid grid-cols-2 gap-4 text-sm">
                {field('Type', p.project_type)}
                {field('Status', p.status?.replace('_', ' '))}
                {field('Project Number', p.project_number)}
                {field('Priority', p.priority)}
                {field('Start Date', p.start_date)}
                {field('End Date', p.end_date)}
                {p.description && (
                  <div className="col-span-2 space-y-1">
                    <p className="text-xs text-muted-foreground">Description</p>
                    <p className="text-sm">{p.description}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3"><CardTitle className="text-sm flex items-center gap-2"><MapPin className="h-4 w-4 text-primary" />Location & Client</CardTitle></CardHeader>
              <CardContent className="grid grid-cols-2 gap-4 text-sm">
                {field('Address', p.address)}
                {field('City', p.city)}
                {field('State / Region', p.state)}
                {field('Country', p.country)}
                {field('Client', p.client_name)}
                {field('Client Email', p.client_email)}
                {field('Client Phone', p.client_phone)}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ── Locations ─────────────────────────────────────────── */}
        <TabsContent value="locations" className="mt-4">
          <LocationsTab projectId={projectId} />
        </TabsContent>

        {/* ── Team ──────────────────────────────────────────────── */}
        <TabsContent value="team" className="mt-4">
          <TeamTab projectId={projectId} orgUsers={orgUsers} />
        </TabsContent>

        {/* ── Budget ────────────────────────────────────────────── */}
        <TabsContent value="budget" className="mt-4">
          <BudgetTab projectId={projectId} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
